export const STORAGE_KEYS = Object.freeze({
  language: 'pixelFlowLang',
  tasks: 'pixelPlannerTasks',
  companies: 'flowdesk_companies',
  healthSettings: 'pixelPlannerHealth',
  soundSettings: 'pixelPlannerSounds',
  governorate: 'pixelFlowGov',
  uiDensity: 'pixelFlowUiDensity',
  theme: 'pixelPlannerTheme',
  userName: 'pixelFlowUserName',
  userPhoto: 'pixelFlowUserPhoto',
  userRole: 'pixelFlowUserRole',
  currentUser: 'flowDeskCurrentUser',
  workspaceUsers: 'flowDeskWorkspaceUsers',
  workspaceName: 'pixelFlowWorkspaceName',
  currentWorkspace: 'flowDeskCurrentWorkspace',
  authSession: 'flowDeskAuthSession',
  prayerStatus: 'pixelPlannerPrayerStatus',
  onboardingDone: 'flowDeskOnboardingDone',
  operationMode: 'flowDeskOperationMode',
  syncQueue: 'flowDeskSyncQueue',
  syncConnection: 'flowDeskSyncConnection',
  syncBackups: 'flowDeskSyncBackups',
  notifications: 'pixelFlowNotifications'
});

export const OPERATION_MODES = Object.freeze({
  local: 'local',
  online: 'online'
});

export const SYNC_OPERATION_TYPES = Object.freeze({
  upsert: 'upsert',
  delete: 'delete',
  replace: 'replace'
});

export const SYNC_OPERATION_STATUS = Object.freeze({
  pending: 'pending',
  syncing: 'syncing',
  synced: 'synced',
  failed: 'failed'
});

export const TASK_STATUSES = Object.freeze({
  scheduled: 'scheduled',
  ready: 'ready',
  inProgress: 'in_progress',
  review: 'review',
  revision: 'revision',
  done: 'done'
});

export const USER_ROLES = Object.freeze({
  owner: 'owner',
  manager: 'manager',
  designer: 'designer',
  client: 'client'
});

export const PERMISSIONS = Object.freeze({
  viewAllTasks: 'viewAllTasks',
  manageTasks: 'manageTasks',
  createTasks: 'createTasks',
  manageReviewFlow: 'manageReviewFlow',
  deleteTasks: 'deleteTasks',
  manageCompany: 'manageCompany',
  manageClients: 'manageClients',
  manageManagers: 'manageManagers',
  viewStats: 'viewStats',
  manageSettings: 'manageSettings',
  backup: 'backup'
});

export const ROLE_PERMISSIONS = Object.freeze({
  [USER_ROLES.owner]: Object.values(PERMISSIONS),
  [USER_ROLES.manager]: [
    PERMISSIONS.viewAllTasks,
    PERMISSIONS.manageTasks,
    PERMISSIONS.createTasks,
    PERMISSIONS.manageReviewFlow,
    PERMISSIONS.deleteTasks,
    PERMISSIONS.viewStats
  ],
  [USER_ROLES.designer]: [
    PERMISSIONS.manageTasks,
    PERMISSIONS.createTasks,
    PERMISSIONS.viewStats
  ],
  [USER_ROLES.client]: [
    PERMISSIONS.viewAllTasks
  ]
});

export const createCurrentUser = ({
  id = 'local-owner',
  name = '',
  email = '',
  photo = null,
  title = '',
  password = '',
  accessRole = USER_ROLES.owner,
  companyIds = [],
  settingsTabs = [],
  clientAccess = {}
} = {}) => ({
  id: id || `user_${Date.now()}`,
  name: name || email.split('@')[0] || 'User',
  email,
  photo,
  title,
  password: password || '',
  accessRole: Object.values(USER_ROLES).includes(accessRole) ? accessRole : USER_ROLES.owner,
  companyIds: Array.isArray(companyIds) ? companyIds.map(String) : [],
  settingsTabs: Array.isArray(settingsTabs) ? settingsTabs.map(String) : [],
  clientAccess: clientAccess && typeof clientAccess === 'object' && !Array.isArray(clientAccess) ? clientAccess : {}
});

export const createWorkspaceUser = ({
  id = null,
  name = '',
  email = '',
  photo = null,
  title = '',
  password = '',
  accessRole = USER_ROLES.designer,
  companyIds = [],
  settingsTabs = [],
  clientAccess = {},
  status = 'active',
  createdAt = null
} = {}) => ({
  id: id || `user_${Date.now()}`,
  name,
  email,
  photo,
  title,
  password: password || '',
  accessRole: Object.values(USER_ROLES).includes(accessRole) ? accessRole : USER_ROLES.designer,
  companyIds: Array.isArray(companyIds) ? companyIds.map(String) : [],
  settingsTabs: Array.isArray(settingsTabs) ? settingsTabs.map(String) : [],
  clientAccess: clientAccess && typeof clientAccess === 'object' && !Array.isArray(clientAccess) ? clientAccess : {},
  status: status || 'active',
  createdAt: createdAt || new Date().toISOString()
});

export const createWorkspace = ({
  id = 'local-workspace',
  name = 'FlowDesk',
  ownerUserId = 'local-owner',
  mode = OPERATION_MODES.online,
  createdAt = null
} = {}) => ({
  id,
  name: name || 'FlowDesk',
  ownerUserId,
  mode: Object.values(OPERATION_MODES).includes(mode) ? mode : OPERATION_MODES.online,
  createdAt: createdAt || new Date().toISOString()
});

export const createAuthSession = ({
  isAuthenticated = false,
  email = '',
  userId = 'local-owner',
  provider = 'local-preview',
  rememberMe = true,
  loggedInAt = null
} = {}) => ({
  isAuthenticated: Boolean(isAuthenticated),
  email,
  userId,
  provider,
  rememberMe: Boolean(rememberMe),
  loggedInAt: loggedInAt || new Date().toISOString()
});

export const createSyncConnection = ({
  enabled = true,
  endpointUrl = '',
  accessToken = '',
  lastConnectedAt = null,
  lastServerRevision = 0,
  autoSyncEnabled = true,
  autoSyncIntervalSeconds = 30
} = {}) => ({
  enabled: Boolean(enabled),
  endpointUrl: endpointUrl || '',
  accessToken: accessToken || '',
  lastConnectedAt,
  lastServerRevision: Number(lastServerRevision || 0),
  autoSyncEnabled: Boolean(autoSyncEnabled),
  autoSyncIntervalSeconds: Math.max(30, Number(autoSyncIntervalSeconds || 30))
});

export const createDefaultCompany = ({ id = '', name = '', theme, lang = 'en' } = {}) => ({
  id,
  name: name || '',
  logo: null,
  systemEnabled: false,
  systemUrl: '',
  driveEnabled: false,
  driveUrl: '',
  theme,
  managers: [],
  clients: [],
  workStart: '09:00',
  workEnd: '17:00',
  workDays: [0, 1, 2, 3, 4, 6],
  overtimeMinutes: 0,
  hourlyRate: 0,
  isClockedIn: false,
  clockInTime: null,
  attendanceLogs: []
});

export const createDefaultTaskDraft = (category = '') => ({
  title: '',
  description: '',
  category,
  accountManager: '',
  client: '',
  designType: 'social',
  selectedPlatforms: [],
  estimatedHours: 1,
  isUrgent: false,
  images: [],
  hasBrief: false,
  brief: '',
  hasTov: false,
  tov: '',
  subtasks: [],
  dueDateType: 'today',
  customDate: '',
  status: TASK_STATUSES.scheduled
});

